import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pandabuy.yachts';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] pt-20 px-4 text-center">
          <i className="ri-file-search-line text-5xl text-foreground-300 mb-4"></i>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">Article Not Found</h1>
          <p className="text-sm text-foreground-500 mb-6">The article you are looking for does not exist.</p>
          <Link to="/" className="px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors">
            Back to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articleUrl = siteUrl + '/article/' + article.slug;

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.subtitle || article.teaserExcerpt,
            "image": article.teaserImage,
            "url": articleUrl,
            "datePublished": "2026-07-01",
            "dateModified": "2026-07-01",
            "author": {
              "@type": "Organization",
              "name": "PandaBuy Yachts",
              "url": siteUrl
            },
            "publisher": {
              "@type": "Organization",
              "name": "PandaBuy Yachts",
              "url": siteUrl,
              "logo": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": articleUrl
            }
          })
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[380px] md:min-h-[460px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url("${article.heroImage}")` }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
        <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center pt-20 pb-14">
          <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-300 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full mb-5">
            {article.tag}
          </span>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-white leading-tight tracking-tight">
            {article.title}
          </h1>
          <p className="mt-4 text-sm md:text-base text-white/70 max-w-xl mx-auto leading-relaxed">
            {article.subtitle}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-full hover:bg-white/20 border border-white/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              Back to Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500 text-white text-sm font-medium rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore Pandabuy Sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="space-y-6">
            {article.sections.map((section, i) => {
              if (section.type === "h2") {
                return (
                  <div key={i}>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-950 pt-2">
                      {section.title}
                    </h2>
                    {section.content && (
                      <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-3">
                        {section.content}
                      </p>
                    )}
                  </div>
                );
              }
              if (section.type === "h3") {
                return (
                  <div key={i}>
                    <h3 className="text-lg md:text-xl font-semibold text-foreground-900 pt-1">
                      {section.title}
                    </h3>
                    {section.content && (
                      <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-2">
                        {section.content}
                      </p>
                    )}
                  </div>
                );
              }
              return (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {section.content}
                </p>
              );
            })}
          </div>

          {/* Internal Navigation */}
          <div className="mt-12 pt-8 border-t border-background-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore Pandabuy Sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}